/* ============================================================
   Talention — SVPT embebido: reducer del espacio

   El estado es un espacio { metodo, departments, levels, positions,
   factors, scales, valuations, ministerio } (ver data.js). La empresa la
   fija registro y el guardado lo hace src/svpt/store.jsx: aquí solo hay
   transiciones puras. Una acción que no cambia nada devuelve el MISMO
   objeto: el store lo usa para no guardar en vano.

   Sin React ni JSX: se puede testear sin montar nada.
   ============================================================ */

import { pruneValuations, applyStandardToFactors, METODOS_VALORACION } from "./data.js";
import { subfactorMinisterio, respuestaValida, otroCompleto, podarRespuestasMinisterio } from "./ministerio.js";
import { canReportTo, romperCiclos, promoteTarget, demoteTarget } from "./hierarchy.js";
import { normalizarNombre } from "./importacion.js";

function reducer(state, action) {
  switch (action.type) {
    /* --- Espacio entero: carga inicial, recarga tras un 409, datos de ejemplo --- */
    case "REPLACE_SPACE":
      return action.space;

    /* --- Departments --- */
    case "ADD_DEPT": {
      const id = Math.max(0, ...state.departments.map(d => d.id)) + 1;
      return { ...state, departments: [...state.departments, { id, ...action.dept, createdAt: nowStr() }] };
    }
    case "UPDATE_DEPT":
      return { ...state, departments: state.departments.map(d => d.id === action.id ? { ...d, ...action.patch } : d) };
    case "DELETE_DEPT":
      return { ...state, departments: state.departments.filter(d => d.id !== action.id) };

    /* --- Levels --- */
    case "ADD_LEVEL": {
      const id = Math.max(0, ...state.levels.map(d => d.id)) + 1;
      return { ...state, levels: [...state.levels, { id, ...action.lvl }] };
    }
    case "UPDATE_LEVEL":
      return { ...state, levels: state.levels.map(d => d.id === action.id ? { ...d, ...action.patch } : d) };
    case "DELETE_LEVEL":
      return { ...state, levels: state.levels.filter(d => d.id !== action.id) };

    /* --- Positions --- */
    case "ADD_POSITION": {
      const id = Math.max(0, ...state.positions.map(p => p.id)) + 1;
      const code = "P" + String(id).padStart(2, "0");
      const pos = {
        id, code,
        description: "",
        reportsToId: null,
        headcount: null,
        area: "",
        centro: "",
        scaleOverrideId: null,
        scaleOverrideReason: "",
        ...action.pos,
        createdAt: nowStr(),
      };
      return { ...state, positions: [...state.positions, pos] };
    }
    case "UPDATE_POSITION": {
      const patch = sanitizeSupervisorPatch(state, action.id, action.patch);
      return { ...state, positions: state.positions.map(p => p.id === action.id ? { ...p, ...patch } : p) };
    }
    case "DELETE_POSITION": {
      const removed = state.positions.find(p => p.id === action.id);
      const heir = removed ? (removed.reportsToId ?? null) : null;
      // Los subordinados no se quedan huérfanos: pasan a depender del
      // superior del puesto eliminado.
      const positions = state.positions
        .filter(p => p.id !== action.id)
        .map(p => p.reportsToId === action.id ? { ...p, reportsToId: heir } : p);
      const valuations = { ...state.valuations };
      delete valuations[action.id];
      const respuestas = { ...state.ministerio.respuestas };
      delete respuestas[action.id];
      return { ...state, positions, valuations, ministerio: { ...state.ministerio, respuestas } };
    }
    case "SET_POSITION_SUPERVISOR": {
      if (!canReportTo(action.positionId, action.reportsToId ?? null, state.positions)) return state;
      return {
        ...state,
        positions: state.positions.map(p =>
          p.id === action.positionId ? { ...p, reportsToId: action.reportsToId ?? null } : p),
      };
    }
    case "DEMOTE_POSITION": {
      const move = demoteTarget(action.positionId, state.positions);
      if (!move.ok) return state;
      return {
        ...state,
        positions: state.positions.map(p =>
          p.id === action.positionId ? { ...p, reportsToId: move.reportsToId } : p),
      };
    }
    case "PROMOTE_POSITION": {
      const move = promoteTarget(action.positionId, state.positions);
      if (!move.ok) return state;
      const actual = state.positions.find(p => p.id === action.positionId);
      const antiguoSuperiorId = actual.reportsToId;
      // El orden del array es el orden entre hermanos, y de ahí sale a quién se
      // engancha "bajar". Recolocando el puesto justo detrás de su antiguo
      // superior, bajar deshace exactamente lo que acaba de hacer subir.
      const resto = state.positions.filter(p => p.id !== action.positionId);
      const movido = { ...actual, reportsToId: move.reportsToId };
      const idx = resto.findIndex(p => p.id === antiguoSuperiorId);
      const positions = idx < 0
        ? [...resto, movido]
        : [...resto.slice(0, idx + 1), movido, ...resto.slice(idx + 1)];
      return { ...state, positions };
    }
    case "SET_POSITION_SCALE_OVERRIDE": {
      const scaleId = action.scaleId ?? null;
      const exists = scaleId != null && state.scales.some(s => s.id === scaleId);
      if (scaleId != null && !exists) return state;
      return {
        ...state,
        positions: state.positions.map(p => p.id === action.positionId
          ? { ...p, scaleOverrideId: scaleId, scaleOverrideReason: scaleId == null ? "" : (action.reason || "") }
          : p),
      };
    }

    /* --- Importación desde Excel (plan de src/svpt/importacion.js) --- */
    case "IMPORT_POSITIONS":
      return importarPuestos(state, action.plan);

    /* --- Factors --- */
    case "SET_FACTOR_SVPT": {
      const groups = state.factors.groups.map(g => g.id === action.groupId ? { ...g, svpt: action.value } : g);
      return { ...state, factors: { ...state.factors, groups } };
    }
    case "AUTOBALANCE_FACTORS": {
      const total = state.factors.groups.reduce((s, g) => s + g.svpt, 0);
      if (total === 100) return state;
      if (total === 0) return state;   // sin nada que repartir, dividir daría NaN
      const groups = state.factors.groups.map(g => ({ ...g, svpt: Math.round((g.svpt / total) * 100) }));
      const diff = 100 - groups.reduce((s, g) => s + g.svpt, 0);
      if (diff !== 0) groups[0].svpt += diff;
      return { ...state, factors: { ...state.factors, groups } };
    }
    case "RESET_FACTORS": {
      // Solo devuelve los pesos de GRUPO a su valor por defecto; la selección de
      // subfactores se queda como esté. Por eso no se borra standardId: si había
      // un estándar aplicado sigue estándolo a medias, y la pantalla debe poder
      // decir "modificado" en vez de fingir que no hay ninguno.
      const groups = state.factors.groups.map(g => ({ ...g, svpt: g.defaultSVPT }));
      return { ...state, factors: { ...state.factors, groups } };
    }
    case "TOGGLE_FACTOR_ITEM": {
      const items = { ...state.factors.items };
      items[action.groupId] = items[action.groupId].map(it =>
        it.id === action.itemId ? { ...it, selected: !it.selected } : it
      );
      return { ...state, factors: { ...state.factors, items } };
    }
    case "SET_ITEM_WEIGHT": {
      const items = { ...state.factors.items };
      items[action.groupId] = items[action.groupId].map(it =>
        it.id === action.itemId ? { ...it, weight: action.value } : it
      );
      return { ...state, factors: { ...state.factors, items } };
    }
    case "EQUALIZE_ITEM_WEIGHTS": {
      const items = { ...state.factors.items };
      const eligible = items[action.groupId].filter(it => it.selected && it.levels.length > 0);
      const eq = eligible.length > 0 ? Math.round(100 / eligible.length) : 0;
      items[action.groupId] = items[action.groupId].map(it =>
        it.selected && it.levels.length > 0 ? { ...it, weight: eq } : it
      );
      return { ...state, factors: { ...state.factors, items } };
    }
    case "UPDATE_FACTOR_ITEM": {
      const items = { ...state.factors.items };
      items[action.groupId] = items[action.groupId].map(it =>
        it.id === action.itemId ? { ...it, ...action.patch } : it
      );
      const factors = { ...state.factors, items };
      // Cambiar los niveles puede dejar respuestas apuntando a un nivel que ya
      // no existe: se limpian para que ningún puesto quede dado por valorado
      // sobre una respuesta fantasma.
      const { valuations } = pruneValuations(state.valuations, factors);
      return { ...state, factors, valuations };
    }
    case "APPLY_FACTOR_STANDARD": {
      const factors = applyStandardToFactors(state.factors, action.standard);
      const { valuations } = pruneValuations(state.valuations, factors);
      return { ...state, factors, valuations };
    }
    case "IMPORT_FACTORS":
      return importarFactores(state, action.plan);
    case "DELETE_FACTOR_ITEM": {
      // Solo los personalizados: el catálogo es fijo, y borrar uno de sus
      // factores rompería los estándares que lo referencian.
      const lista = state.factors.items[action.groupId] || [];
      const item = lista.find(it => it.id === action.itemId);
      if (!item || !item.personalizado) return state;
      const items = { ...state.factors.items, [action.groupId]: lista.filter(it => it.id !== action.itemId) };
      const factors = { ...state.factors, items };
      const { valuations } = pruneValuations(state.valuations, factors);
      return { ...state, factors, valuations };
    }

    /* --- Scales --- */
    case "UPDATE_SCALE":
      return { ...state, scales: state.scales.map(s => s.id === action.id ? { ...s, ...action.patch } : s) };
    case "ADD_SCALE": {
      const id = Math.max(0, ...state.scales.map(s => s.id)) + 1;
      return { ...state, scales: [...state.scales, { id, ...action.scale }] };
    }
    case "DELETE_SCALE": {
      // Los puestos fijados a mano a esa escala vuelven al cálculo automático.
      const positions = state.positions.map(p =>
        p.scaleOverrideId === action.id ? { ...p, scaleOverrideId: null, scaleOverrideReason: "" } : p);
      return { ...state, scales: state.scales.filter(s => s.id !== action.id), positions };
    }

    /* --- Valuations --- */
    case "SET_VALUATION": {
      const cur = state.valuations[action.positionId] || {};
      const valuations = { ...state.valuations, [action.positionId]: { ...cur, [action.itemId]: action.levelId } };
      return { ...state, valuations };
    }

    /* --- Método de valoración (una empresa, un método) --- */
    case "CAMBIAR_METODO": {
      if (!METODOS_VALORACION.includes(action.metodo) || action.metodo === state.metodo) return state;
      // Se vacían las respuestas del método que se abandona; la configuración
      // de factores Talention y los «Otros» del Ministerio se conservan.
      if (action.metodo === "ministerio") return { ...state, metodo: "ministerio", valuations: {} };
      return { ...state, metodo: "talention", ministerio: { ...state.ministerio, respuestas: {} } };
    }
    case "SET_RESPUESTA_MINISTERIO": {
      if (state.metodo !== "ministerio") return state;
      if (!state.positions.some(p => p.id === action.positionId)) return state;
      const sf = subfactorMinisterio(action.codigo);
      // Solo entra lo que la poda conservaría al recargar.
      if (!sf || !respuestaValida(sf, action.respuesta)) return state;
      if (sf.otros && state.ministerio.otros[sf.codigo]?.activo !== true) return state;
      const actuales = state.ministerio.respuestas[action.positionId] || {};
      const almacenada = actuales[sf.codigo];
      // Si la respuesta ya almacenada es idéntica, no cambia nada: devolver el mismo estado.
      if (almacenada !== undefined) {
        if (typeof action.respuesta === "number" && almacenada === action.respuesta) return state;
        if (typeof action.respuesta === "object" && almacenada.eje1 === action.respuesta.eje1 && almacenada.eje2 === action.respuesta.eje2) return state;
      }
      return {
        ...state,
        ministerio: {
          ...state.ministerio,
          respuestas: { ...state.ministerio.respuestas, [action.positionId]: { ...actuales, [sf.codigo]: action.respuesta } },
        },
      };
    }
    case "SET_OTRO_MINISTERIO": {
      const actual = state.ministerio.otros[action.codigo];
      if (!actual) return state;
      const otro = { ...actual, ...action.patch };
      // Un «Otros» activo siempre tiene nombre y sus 5 niveles.
      if (otro.activo && !otroCompleto(otro)) return state;
      // Si nada cambió, devolver el mismo estado.
      const cambioActivo = otro.activo !== actual.activo;
      const cambioNombre = otro.nombre !== actual.nombre;
      const cambioJustificacion = otro.justificacion !== actual.justificacion;
      const cambioNiveles = !otro.niveles || !actual.niveles || otro.niveles.length !== actual.niveles.length || otro.niveles.some((n, i) => n !== actual.niveles[i]);
      if (!cambioActivo && !cambioNombre && !cambioJustificacion && !cambioNiveles) return state;
      const otros = { ...state.ministerio.otros, [action.codigo]: otro };
      // Al desactivarlo, sus respuestas desaparecen en todos los puestos.
      const { respuestas } = podarRespuestasMinisterio(state.ministerio.respuestas, otros);
      return { ...state, ministerio: { ...state.ministerio, otros, respuestas } };
    }
    default:
      return state;
  }
}

/* Un puesto no puede pasar a depender de sí mismo ni de un subordinado suyo:
   crearía un ciclo y el organigrama dejaría de ser un árbol. Si el patch trae
   un superior imposible se descarta ese campo y el resto se aplica igual. */
function sanitizeSupervisorPatch(state, positionId, patch) {
  if (!patch || !("reportsToId" in patch)) return patch;
  const reportsToId = patch.reportsToId ?? null;
  if (canReportTo(positionId, reportsToId, state.positions)) return { ...patch, reportsToId };
  const { reportsToId: _imposible, ...rest } = patch;
  return rest;
}

function nowStr() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* Añade los factores de un plan de importación como personalizados:
   desactivados y con peso 0, para no mover la puntuación de lo ya valorado.
   El prefijo `x-` no choca con los ids del catálogo (k-, r-, e-, c-). Un
   factor que entre la vista previa y la confirmación ya existe se salta. */
function importarFactores(state, plan) {
  if (plan.crear.length === 0) return state;
  const items = { ...state.factors.items };
  const usados = Object.values(items).flat()
    .map(it => /^x-(\d+)$/.exec(it.id))
    .filter(Boolean)
    .map(m => Number(m[1]));
  let siguiente = Math.max(0, ...usados) + 1;
  for (const c of plan.crear) {
    const lista = items[c.groupId] || [];
    if (lista.some(it => normalizarNombre(it.name) === normalizarNombre(c.item.name))) continue;
    items[c.groupId] = [...lista, { id: `x-${siguiente++}`, ...c.item, selected: false, weight: 0, personalizado: true }];
  }
  return { ...state, factors: { ...state.factors, items } };
}

/* Aplica un plan de importación de puestos en un único estado nuevo, así
   el resultado se guarda de una vez. Orden: catálogos, puestos nuevos,
   actualizaciones y, por último, superiores (los que decide el plan, con una
   red de seguridad contra ciclos). Un puesto a actualizar que ya no existe se
   ignora: el espacio pudo recargarse entre la vista previa y la confirmación. */
function importarPuestos(state, plan) {
  const actualizables = plan.actualizar.filter(a => state.positions.some(p => p.id === a.id));
  if (plan.crear.length === 0 && actualizables.length === 0) return state;
  const ahora = nowStr();

  const crearCatalogo = (lista, nuevos, crear) => {
    const ids = new Map();
    const resultado = [...lista];
    for (const n of nuevos) {
      const existente = resultado.find(x => normalizarNombre(x.name) === n.clave);
      if (existente) { ids.set(n.clave, existente.id); continue; }
      const id = Math.max(0, ...resultado.map(x => x.id)) + 1;
      resultado.push(crear(id, n.name, resultado));
      ids.set(n.clave, id);
    }
    return { lista: resultado, ids };
  };
  const deps = crearCatalogo(state.departments, plan.departamentosNuevos,
    (id, name) => ({ id, name, description: "", createdAt: ahora }));
  const niveles = crearCatalogo(state.levels, plan.nivelesNuevos,
    (id, name, lista) => ({ id, name, description: "", order: Math.max(0, ...lista.map(l => l.order || 0)) + 1 }));
  const idDe = (ref, ids) => (ref == null ? null : "id" in ref ? ref.id : ids.get(ref.nuevo) ?? null);

  const positions = [...state.positions];
  const idPuesto = new Map();
  for (const c of plan.crear) {
    const id = Math.max(0, ...positions.map(p => p.id)) + 1;
    positions.push({
      id,
      code: "P" + String(id).padStart(2, "0"),
      name: c.name,
      departmentId: idDe(c.departamento, deps.ids),
      levelId: idDe(c.nivel, niveles.ids),
      reportsToId: null,
      headcount: c.personas,
      description: c.descripcion ?? "",
      scaleOverrideId: null,
      scaleOverrideReason: "",
      createdAt: ahora,
    });
    idPuesto.set(c.clave, id);
  }

  for (const a of actualizables) {
    const i = positions.findIndex(p => p.id === a.id);
    const patch = {};
    if (a.departamento) patch.departmentId = idDe(a.departamento, deps.ids);
    if (a.nivel) patch.levelId = idDe(a.nivel, niveles.ids);
    if (a.descripcion != null) patch.description = a.descripcion;
    if (a.personas != null) patch.headcount = a.personas;
    positions[i] = { ...positions[i], ...patch };
  }

  const conSuperior = [
    ...plan.crear.map(c => ({ fila: c.fila, id: idPuesto.get(c.clave), superior: c.superior })),
    ...actualizables.map(a => ({ fila: a.fila, id: a.id, superior: a.superior })),
  ].filter(e => e.superior != null).sort((x, y) => x.fila - y.fila);
  // El plan ya decidió qué superiores valen (ciclos juzgados sobre el estado
  // final). Aquí se aplican todos y solo queda una red de seguridad: si el
  // estado real difiere del planificado y aparece un ciclo, se deshace el de
  // la fila más tardía.
  const pedidos = [];
  for (const e of conSuperior) {
    const superiorId = idDe(e.superior, idPuesto);
    // El superior pudo borrarse entre la vista previa y la confirmación.
    if (superiorId == null || !positions.some(p => p.id === superiorId)) continue;
    const i = positions.findIndex(p => p.id === e.id);
    pedidos.push({ id: e.id, fila: e.fila, anterior: positions[i].reportsToId });
    positions[i] = { ...positions[i], reportsToId: superiorId };
  }
  const nodos = positions.map(p => ({ id: p.id, reportsToId: p.reportsToId }));
  for (const id of romperCiclos(nodos, pedidos)) {
    const i = positions.findIndex(p => p.id === id);
    positions[i] = { ...positions[i], reportsToId: pedidos.find(p => p.id === id).anterior };
  }

  return { ...state, departments: deps.lista, levels: niveles.lista, positions };
}

export { reducer };
